import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { fbt } from 'fbtee'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useAuth } from '../../hooks/useAuth'
import { useFriendsOverview } from '../../hooks/useFriendsOverview'
import { usePendingGroupInvites } from '../../hooks/usePendingGroupInvites'
import { useInboxSuggestions } from './useInboxSuggestions'
import { useCrewTicketActions } from '../../hooks/useCrewTicketActions'
import { usePageLoadTrace } from '../../lib/perf'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { PageLayout } from '../../components/PageLayout'
import { RegionHeaderChip } from '../../components/RegionHeaderChip'
import { InviteFriendDialog } from '../../components/InviteFriendDialog'
import { CrewInbox } from './CrewInbox'
import { GroupsSection } from './GroupsSection'
import { FriendsStrip } from './FriendsStrip'
import { CrewActivitySection } from './CrewActivitySection'


/**
 * The Crew tab: your people and whatever is waiting on you — requests and
 * invites first, then groups, friends, and what the crew has been planning lately.
 */
export default function SocialPage() {
  const [searchParams] = useSearchParams()
  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const navigate = useTransitionNavigate()

  const { signedInUid, loading: authLoading } = useAuth()
  const overview = useFriendsOverview()
  const { pendingInvites, acceptInvite, rejectInvite } = usePendingGroupInvites(signedInUid)
  const inboxSuggestions = useInboxSuggestions(signedInUid)
  const ticketActions = useCrewTicketActions(signedInUid)

  useDocumentTitle(pageTitle(fbt('Crew', 'Browser tab title for the Crew tab')))
  usePageLoadTrace('social_page_to_content', !authLoading && !overview.loading)

  // The friend-request push and old links open the friends list directly.
  if (searchParams.get('tab') === 'friends') return <Navigate to="/friends" replace />

  return (
    <PageLayout leading={<RegionHeaderChip />}>
      {signedInUid && (
        <CrewInbox
          requestIds={overview.incomingIds}
          profiles={overview.profiles}
          profilesLoading={overview.profilesLoading}
          onAcceptRequest={uid => overview.acceptRequest(uid)}
          onDeclineRequest={uid => overview.removeFriend(uid)}
          invites={pendingInvites}
          onAcceptInvite={invite => acceptInvite(invite).then(() => navigate(`/groups/${invite.groupId}`, { state: { entryPoint: 'invite' } }))}
          onDeclineInvite={invite => rejectInvite(invite)}
          suggestions={inboxSuggestions.suggestions}
          onApproveSuggestion={s => void inboxSuggestions.approve(s).catch(() => {})}
          onDeclineSuggestion={s => void inboxSuggestions.decline(s).catch(() => {})}
          viewerId={signedInUid}
          tickets={ticketActions.items}
          ticketProfiles={ticketActions.profiles}
        />
      )}

      <GroupsSection
        createOpen={createGroupOpen}
        onCreateOpenChange={setCreateGroupOpen}
        friendInterests={overview.friendInterests}
        myStatuses={overview.myStatuses}
        profiles={overview.profiles}
      />

      {signedInUid && (
        <FriendsStrip
          friends={overview.friends}
          profiles={overview.profiles}
          loading={overview.loading}
          onAdd={() => setInviteOpen(true)}
        />
      )}

      {signedInUid && <CrewActivitySection />}

      <InviteFriendDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </PageLayout>
  )
}
